import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { projectKeys } from './api'

/**
 * Sprintler (proje, tarih aralığı, hedef, personel ve kapasite; projede tek etkin sprint) ve atama talepleri: doğrudan
 * atama yetkisi olmayanın ataması ya da etkin sprintteki personele ek görev; atanan, proje lideri ve firma sahibi onaylar.
 */
export const SPRINT_PERMS = { manage: 'tasks.sprint.manage', assignDirect: 'tasks.assign.direct', approveOwner: 'tasks.assignment.approve' } as const

export type SprintStatus = 'planned' | 'active' | 'closed'
export type SprintMember = { userId: string; capacityHours: number | null }
export type Sprint = {
  id: string
  projectId: string
  name: string
  goal: string
  startDate: string
  endDate: string
  status: SprintStatus
  members: SprintMember[]
  tasks: number
  doneTasks: number
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const n = (v: unknown) => Number(v) || 0
const cap = (v: unknown) => (v == null || v === '' ? null : Number(v))

const mapSprint = (r: Row): Sprint => ({
  id: s(r.id),
  projectId: s(r.project_id),
  name: s(r.name),
  goal: s(r.goal),
  startDate: s(r.start_date).slice(0, 10),
  endDate: s(r.end_date).slice(0, 10),
  status: (['planned', 'active', 'closed'].includes(s(r.status)) ? s(r.status) : 'planned') as SprintStatus,
  members: asList(r.members as Row[]).map((m) => ({ userId: s(m.user_id), capacityHours: cap(m.capacity_hours) })),
  tasks: n(r.tasks),
  doneTasks: n(r.done_tasks),
})

const sprintKey = (projectId: string) => ['projects', projectId, 'sprints'] as const

export const useSprints = (projectId: string, enabled = true) =>
  useQuery({ queryKey: sprintKey(projectId), enabled: enabled && !!projectId, queryFn: async () => asList(await api.get<Row[]>('/api/v1/sprints', { project_id: projectId })).map(mapSprint) })

export type MemberLoad = { userId: string; capacityHours: number | null; tasks: number; doneTasks: number; estimated: number; doneEstimated: number; spent: number; over: boolean }
export type SprintReport = {
  sprint: Sprint
  tasks: number
  doneTasks: number
  estimated: number
  doneEstimated: number
  spent: number
  addedAfterStart: number
  unassigned: number
  members: MemberLoad[]
}

export const useSprintReport = (id: string | null) =>
  useQuery({
    queryKey: ['sprints', 'report', id],
    enabled: !!id,
    queryFn: async (): Promise<SprintReport> => {
      const r = await api.get<Row>(`/api/v1/sprints/${id}/report`)
      return {
        sprint: mapSprint(r.sprint as Row),
        tasks: n(r.tasks),
        doneTasks: n(r.done_tasks),
        estimated: n(r.estimated_hours),
        doneEstimated: n(r.done_estimated_hours),
        spent: n(r.spent_hours),
        addedAfterStart: n(r.added_after_start),
        unassigned: n(r.unassigned),
        members: asList(r.members as Row[]).map((m) => ({
          userId: s(m.user_id),
          capacityHours: cap(m.capacity_hours),
          tasks: n(m.tasks),
          doneTasks: n(m.done_tasks),
          estimated: n(m.estimated_hours),
          doneEstimated: n(m.done_estimated_hours),
          spent: n(m.spent_hours),
          over: m.over_capacity === true,
        })),
      }
    },
  })

export type SprintInput = { name: string; goal: string; startDate: string; endDate: string; members: SprintMember[] }

function useSprintMut<A>(projectId: string, fn: (a: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: sprintKey(projectId) }),
        qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }),
        qc.invalidateQueries({ queryKey: ['sprints'] }),
      ]),
  })
}

const sprintBody = (projectId: string, i: SprintInput) => ({
  project_id: projectId,
  name: i.name.trim(),
  goal: i.goal.trim(),
  start_date: i.startDate,
  end_date: i.endDate,
  members: i.members.map((m) => ({ user_id: m.userId, capacity_hours: m.capacityHours })),
})

export const useSaveSprint = (projectId: string) =>
  useSprintMut(projectId, ({ id, input }: { id?: string; input: SprintInput }) =>
    id ? api.put(`/api/v1/sprints/${id}`, sprintBody(projectId, input)) : api.post('/api/v1/sprints', sprintBody(projectId, input)),
  )

export const useStartSprint = (projectId: string) => useSprintMut(projectId, (id: string) => api.post(`/api/v1/sprints/${id}/start`, {}))

export const useCloseSprint = (projectId: string) =>
  useSprintMut(projectId, ({ id, moveTo }: { id: string; moveTo: string }) => api.post<{ moved: number }>(`/api/v1/sprints/${id}/close`, { move_to_sprint_id: moveTo }))

// --- atama talepleri ---

export type ApprovalRole = 'assignee' | 'lead' | 'owner'
export type Approval = { role: ApprovalRole; approvers: string[]; decision: '' | 'approved' | 'rejected'; decidedBy: string; note: string; decidedAt: string }
export type AssignmentStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'
export type AssignmentRequest = {
  id: string
  taskId: string
  taskKey: string
  taskTitle: string
  projectId: string
  assigneeId: string
  sprintId: string
  requestedBy: string
  reason: string
  status: AssignmentStatus
  createdAt: string
  approvals: Approval[]
  myRoles: ApprovalRole[]
  canCancel: boolean
}

const mapRequest = (r: Row): AssignmentRequest => ({
  id: s(r.id),
  taskId: s(r.task_id),
  taskKey: s(r.task_key),
  taskTitle: s(r.task_title),
  projectId: s(r.project_id),
  assigneeId: s(r.assignee_user_id),
  sprintId: s(r.sprint_id),
  requestedBy: s(r.requested_by),
  reason: s(r.reason),
  status: s(r.status) as AssignmentStatus,
  createdAt: s(r.created_at),
  approvals: asList(r.approvals as Row[]).map((a) => ({
    role: s(a.role) as ApprovalRole,
    approvers: asList(a.approvers as string[]).map(String),
    decision: (s(a.decision) || '') as Approval['decision'],
    decidedBy: s(a.decided_by),
    note: s(a.note),
    decidedAt: s(a.decided_at),
  })),
  myRoles: asList(r.my_roles as string[]).map((x) => x as ApprovalRole),
  canCancel: r.can_cancel === true,
})

export const assignmentKeys = { all: ['assignment-requests'] as const }

export const useAssignmentRequests = (f: { scope?: 'pending_me' | 'mine' | ''; taskId?: string; status?: string }, enabled = true) =>
  useQuery({
    queryKey: [...assignmentKeys.all, f],
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/tasks/assignment-requests', { scope: f.scope ?? '', task_id: f.taskId ?? '', status: f.status ?? '' })).map(mapRequest),
  })

function useAssignMut<A>(fn: (a: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: assignmentKeys.all }), qc.invalidateQueries({ queryKey: projectKeys.all })]),
  })
}

export const useDecideAssignment = () =>
  useAssignMut(({ id, decision, note }: { id: string; decision: 'approved' | 'rejected'; note: string }) =>
    api.post(`/api/v1/tasks/assignment-requests/${id}/decide`, { decision, note: note.trim() }),
  )

export const useCancelAssignment = () => useAssignMut((id: string) => api.post(`/api/v1/tasks/assignment-requests/${id}/cancel`, {}))

export const useRequestAssignment = () =>
  useAssignMut(({ taskId, assigneeId, sprintId, reason }: { taskId: string; assigneeId: string; sprintId: string; reason: string }) =>
    api.post<Row>(`/api/v1/tasks/${taskId}/assignment-requests`, { assignee_user_id: assigneeId, sprint_id: sprintId, reason: reason.trim() }),
  )
